// API Configuration
// This file centralizes all API-related configuration

// Default configuration (non-URL defaults)
const defaultConfig = {
  apiVersion: 'v1',
  timeout: 10000, // 10 seconds
  endpoints: {
    auth: {
      login: '/auth/login',
      refresh: '/auth/refresh',
      logout: '/auth/logout',
    },
    // Add more endpoints as needed
    faceDetection: {
      detect: '/face-detection/detect',
      upload: '/face-detection/upload',
    },
  },
};

// Environment-specific overrides
const getEnvironmentConfig = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  
  switch (env) {
    case 'production':
      return {
        // Empty = same-origin, so one build serves both the LAN IP and the
        // Cloudflare-tunnelled domain through nginx (/api → :8000).
        baseURL: (process.env.REACT_APP_API_BASE_URL || '').trim(),
        timeout: 15000,
      };
    case 'intranet':
      return {
        baseURL: process.env.REACT_APP_API_BASE_URL || 'http://nano02.local',
        timeout: 12000,
      };
    case 'development':
    default:
      return {
        // In development, pick from .env.development (CRA loads REACT_APP_*).
        // Prefer 127.0.0.1 over localhost to avoid IPv6 (::1) colliding with Cursor on :8000.
        baseURL: process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000',
        timeout: 10000,
      };
  }
};

// Merge default config with environment-specific config
const config = {
  ...defaultConfig,
  ...getEnvironmentConfig(),
};

const stripTrailingSlash = (value = '') => value.replace(/\/+$/, '');
const ensureLeadingSlash = (value = '') => (value.startsWith('/') ? value : `/${value}`);

const getApiSegment = () => `/api/${config.apiVersion}`;

// Helper function to build full API URL.
// Endpoints may be relative (`/recording/...`) or already versioned (`/api/v1/recording/...`).
// Base URL may be host-only, end with `/api`, or already include `/api/v1`.
// Always normalize so we never produce `/api/v1/api/v1/...`.
export const buildApiUrl = (endpoint) => {
  const normalizedBase = stripTrailingSlash(config.baseURL);
  let path = ensureLeadingSlash(endpoint);
  const apiSegment = getApiSegment();

  // Strip a leading /api/v1 from the path — the base-URL branch below adds it when needed.
  if (path === apiSegment || path.startsWith(`${apiSegment}/`)) {
    path = path.slice(apiSegment.length) || '/';
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
  }

  if (!normalizedBase) {
    return `${apiSegment}${path}`;
  }

  if (normalizedBase.endsWith(apiSegment)) {
    return `${normalizedBase}${path}`;
  }

  // Production default is sometimes `https://host/api` (version not included).
  if (normalizedBase.endsWith('/api')) {
    return `${normalizedBase}/${config.apiVersion}${path}`;
  }

  return `${normalizedBase}${apiSegment}${path}`;
};

// DB Explorer is mounted at /api/debug/db-explorer (not under /api/v1).
export const buildDebugExplorerUrl = (endpoint) => {
  let normalizedBase = stripTrailingSlash(config.baseURL);
  normalizedBase = normalizedBase.replace(/\/api\/v1$/, '').replace(/\/api$/, '');
  const normalizedEndpoint = ensureLeadingSlash(endpoint);
  return `${normalizedBase}/api/debug/db-explorer${normalizedEndpoint}`;
};

// Export the configuration
export default config;

// Export individual parts for convenience
export const { baseURL, apiVersion, timeout, endpoints } = config;
